import { insuranceCardData } from '@/components/organisms/InsuranceFinderCard/helpers'
import { Categories, ThemeColor } from '@/enums'
import { TickIcon } from '@/icons'
import { useItemDialog, useGoToPath } from '@/utils'
import { Button } from '@surein/ui'
import { Upload } from 'lucide-react'
import { ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  Column,
  FilePicker,
  Row,
  ShowWhen,
  Typography,
} from '../../../components'
import { paths } from '../../../routes/paths'
import { Graphic } from '../Graphic'
import { UploadContractModal } from '../UploadContractModal'
import { useMessages } from '../helpers'
import {
  CardFeaturesContainer,
  DefaultInsuranceDetails,
  InsuranceDefaultViewWrapper,
  PageActions,
  TopCardTitle,
} from '../styles'

interface FinderSectionTranslations {
  topCard: {
    title: string
  }
  sectionOneTitle: string
  sectionOneDescription?: string
  sectionOneDescriptionList?: string[]
  sectionOneExtendedDescription?: string
  sectionTwoTitle: string
  sectionTwoDescription: string
  sectionThreeTitle?: string
  sectionThreeDescription?: string
  featuresTitle?: string
  featuresList: Array<{
    title: string
    description?: string
  }>
  contactExpertData: {
    title: string
    description: string
    buttonText: string
  }
}

interface InsuranceCustomBaseViewProps {
  popoverContent?: ReactNode
  graphicSection?: 1 | 2
}

export const InsuranceCustomBaseView = ({
  popoverContent,
  graphicSection = 1,
}: InsuranceCustomBaseViewProps) => {
  const { categoryId, locationId } = useParams<{
    categoryId: keyof typeof Categories
    locationId: string
  }>()
  const messages = useMessages(categoryId!)
  const contentTranslations = (messages.finderSectionTranslations as unknown) as FinderSectionTranslations

  const {
    item: files,
    openItemDialog,
    isOpen,
    closeItemDialog,
  } = useItemDialog<File[]>([])

  const goToFindInsurances = useGoToPath(
    paths.findInsurancesWithKey(locationId || ''),
  )

  if (!categoryId) {
    return null
  }

  const { featureIcons = [] } = insuranceCardData[categoryId] || {}

  return (
    <InsuranceDefaultViewWrapper>
      <DefaultInsuranceDetails>
        <Card dark>
          <Column gap="16px">
            <TopCardTitle>
              <Typography variant="h4">
                {contentTranslations.topCard.title}
              </Typography>
            </TopCardTitle>
            {messages.features.map((feature: string, i: number) => {
              const Icon = featureIcons[i]

              return (
                <Row key={feature} gap="16px">
                  <div>
                    <Icon color={ThemeColor.b50} size="button" stroke />
                  </div>
                  <Typography variant="p2Body">{feature}</Typography>
                </Row>
              )
            })}
          </Column>
        </Card>
        <Column gap="28px">
          <Typography variant="p1Body">
            {contentTranslations.sectionOneTitle}
          </Typography>
          <ShowWhen when={Boolean(contentTranslations.sectionOneDescription)}>
            <Typography color={ThemeColor.b50} variant="p1Body">
              {contentTranslations.sectionOneDescription}
            </Typography>
          </ShowWhen>
          <ShowWhen
            when={Boolean(
              contentTranslations.sectionOneDescriptionList?.length,
            )}
          >
            <ul>
              {contentTranslations.sectionOneDescriptionList?.map(
                (item: string, index: number) => (
                  <li key={index}>
                    <Typography color={ThemeColor.b50} variant="p1Body">
                      {item}
                    </Typography>
                  </li>
                ),
              )}
            </ul>
          </ShowWhen>
          <ShowWhen when={graphicSection === 1}>
            <Graphic categoryId={categoryId} popoverContent={popoverContent} />
          </ShowWhen>
          <ShowWhen
            when={Boolean(contentTranslations.sectionOneExtendedDescription)}
          >
            <Typography color={ThemeColor.b50} variant="p1Body">
              {contentTranslations.sectionOneExtendedDescription}
            </Typography>
          </ShowWhen>
        </Column>
        <Column gap="16px">
          <Typography variant="p1Body">
            {contentTranslations.sectionTwoTitle}
          </Typography>
          <Typography color={ThemeColor.b50} variant="p1Body">
            {contentTranslations.sectionTwoDescription}
          </Typography>
        </Column>
        <ShowWhen when={graphicSection === 2}>
          <Graphic categoryId={categoryId} popoverContent={popoverContent} />
        </ShowWhen>
        <ShowWhen
          when={Boolean(
            contentTranslations.sectionThreeTitle ||
              contentTranslations.sectionThreeDescription,
          )}
        >
          <Column gap="16px">
            <Typography variant="p1Body">
              {contentTranslations.sectionThreeTitle}
            </Typography>
            <Typography color={ThemeColor.b50} variant="p1Body">
              {contentTranslations.sectionThreeDescription}
            </Typography>
          </Column>
        </ShowWhen>
        <Column gap="16px">
          <ShowWhen when={Boolean(contentTranslations.featuresTitle)}>
            <Typography variant="p1Body">
              {contentTranslations.featuresTitle}
            </Typography>
          </ShowWhen>
          <CardFeaturesContainer>
            {contentTranslations.featuresList.map(
              (
                item: { title: string; description?: string },
                index: number,
              ) => (
                <Row key={index} alignItems="start" gap="16px">
                  <TickIcon size="title" />
                  <Column>
                    <Typography variant="p1Body">{item.title}</Typography>
                    <ShowWhen when={Boolean(item.description)}>
                      <Typography color={ThemeColor.b50} variant="p1Body">
                        {item.description}
                      </Typography>
                    </ShowWhen>
                  </Column>
                </Row>
              ),
            )}
          </CardFeaturesContainer>
        </Column>
        <Card dark padding="20px">
          <Column gap="16px">
            <Typography color={ThemeColor.b100} variant="p1Body">
              {contentTranslations.contactExpertData.title}
            </Typography>
            <Typography color={ThemeColor.b50} variant="p1Body">
              {contentTranslations.contactExpertData.description}
            </Typography>
            <Row>
              <Button
                onClick={() => goToFindInsurances()}
                variant="primary"
                shape="square"
              >
                {contentTranslations.contactExpertData.buttonText}
              </Button>
            </Row>
          </Column>
        </Card>
        <PageActions>
          <Column alignItems="flex-start" gap="32px">
            <Column gap="16px">
              <Typography responsive variant="p1Body">
                {messages.haveAContract}
              </Typography>
              <Typography color={ThemeColor.b50} variant="p1Body">
                {messages.uploadContract}
              </Typography>
            </Column>
            <FilePicker accept=".pdf,.jpg" onPick={openItemDialog}>
              <Button shape="square" variant="muted" suffixIcon={Upload}>
                {messages.uploadExistingContract}
              </Button>
            </FilePicker>
          </Column>
        </PageActions>
      </DefaultInsuranceDetails>
      {isOpen && (
        <UploadContractModal files={files} onClose={closeItemDialog} />
      )}
    </InsuranceDefaultViewWrapper>
  )
}
